import {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  type FormEvent,
  type MouseEvent,
} from 'react'
import styles from './ai-search-input.module.css'
import AiSearchIcon from './assets/ai-search.svg'
import CloseIcon from './assets/close.svg'
import ArrowIcon from './assets/arrow-right.svg'
import cx from 'classnames'
import { AI_SEARCH_MAX_QUERY_LENGTH } from '../../constants/search-input'

type AiSearchInputProps = {
  onSearch?: (message: string) => void | Promise<void>
  onInput?: (event: FormEvent<HTMLInputElement>) => void
  onFocus?: (event: FormEvent<HTMLInputElement>) => void
  className?: string
  showSubmitButton?: boolean
  placeholders?: string[]
  placeholder?: string
  'aria-label'?: string
  value?: string
  defaultValue?: string
  maxLength?: number
}

export type AiSearchInputRef = {
  focus: () => void
  blur: () => void
}

// prevent focus on clear button when clicking on it, so that the input remains focused
const onClearMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
  event.preventDefault()
}

export const AiSearchInput = forwardRef<AiSearchInputRef, AiSearchInputProps>(
  (props: AiSearchInputProps, ref) => {
    const [isLoading, setIsLoading] = useState(false)
    const containerRef = useRef<HTMLFormElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Expose focus and blur methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus()
      },
      blur: () => {
        inputRef.current?.blur()
      },
    }))

    const {
      className = '',
      showSubmitButton = true,
      placeholders = [],
      maxLength = AI_SEARCH_MAX_QUERY_LENGTH,
      ...inputProps
    } = props
    const onInputCallback = (
      typeof props.onInput === 'function' ? props.onInput : undefined
    ) as AiSearchInputProps['onInput']

    const onInputFocusCallback = (
      typeof props.onFocus === 'function' ? props.onFocus : undefined
    ) as AiSearchInputProps['onFocus']

    const scrollingPlaceholders =
      placeholders.length > 1 ? placeholders : undefined
    const placeholder = scrollingPlaceholders
      ? undefined
      : placeholders[0] ??
        (inputProps.placeholder as AiSearchInputProps['placeholder'])
    const ariaLabel =
      props['aria-label'] ?? scrollingPlaceholders?.[0] ?? placeholder

    const toggleHasValueClass = (hasValue: boolean) => {
      if (!containerRef.current) {
        return
      }

      containerRef.current.classList.toggle(styles.hasValue, hasValue)
    }

    const onFocusHandler = () => {
      containerRef.current?.classList.add(styles.hasFocus)
    }

    const onBlurHandler = () => {
      containerRef.current?.classList.remove(styles.hasFocus)
    }

    const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const query = (inputRef.current?.value ?? '').slice(0, maxLength)
      const result = props.onSearch?.(query) as void | Promise<void>

      // check if the result is a Promise to handle loading state
      if (result instanceof Promise) {
        setIsLoading(true)
        try {
          await result
        } finally {
          setIsLoading(false)
        }
      }
    }

    const onInputHandler = (event: FormEvent<HTMLInputElement>) => {
      toggleHasValueClass(event.currentTarget.value.length > 0)
      onInputCallback?.(event)
    }

    const onClearClick = () => {
      if (!inputRef.current) {
        return
      }

      inputRef.current.value = ''
      toggleHasValueClass(false)
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
      inputRef.current.focus()
    }

    const containerClass = `${styles.container} ${
      !!inputProps.value || !!inputProps.defaultValue ? styles.hasValue : ''
    }`.trim()

    return (
      <form
        ref={containerRef}
        className={containerClass}
        onSubmit={onSubmitHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
      >
        <AiSearchIcon />
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            maxLength={maxLength}
            className={`${styles.input} ${className}`.trim()}
            placeholder={placeholder}
            aria-label={ariaLabel}
            onFocus={onInputFocusCallback}
            onInput={onInputHandler}
            autoComplete="off"
          />
          {scrollingPlaceholders && (
            <div
              aria-hidden="true"
              className={cx(
                styles.scrollingPlaceholders,
                styles[
                  `scrollCount${Math.min(
                    Math.max(scrollingPlaceholders.length, 2),
                    5,
                  )}`
                ],
              )}
            >
              {scrollingPlaceholders.map((text, index) => (
                <span key={`${index}-${text}`}>{text}</span>
              ))}
            </div>
          )}
        </div>
        {isLoading ? (
          <svg
            className={styles.loadingSpinner}
            xmlns="https://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <circle
              cx="16"
              cy="16"
              r="11"
              className={styles.backgroundCircle}
            />
            <path
              d="M5,16a11,11 0 1,0 22,0a11,11 0 1,0 -22,0"
              className={styles.foregroundCircle}
            />
          </svg>
        ) : (
          <>
            <button
              className={cx(styles.actionButton, styles.clearButton)}
              type="button"
              aria-label="clear search"
              onMouseDown={onClearMouseDown}
              onClick={onClearClick}
            >
              <CloseIcon aria-hidden="true" />
            </button>
            {showSubmitButton && (
              <button
                className={cx(styles.actionButton, styles.submitButton)}
                type="submit"
              >
                <ArrowIcon aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </form>
    )
  },
)

AiSearchInput.displayName = 'AiSearchInput'

export default AiSearchInput
